import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { api } from "../api/rest";
import type { OrdersResponse } from "../api/types";
import type { TimelineOrders } from "../lib/timeline";
import { marketOrders } from "../store/orders";
import { useOrders } from "../store/useOrders";
import { useMarketPnl } from "./usePnl";

/**
 * Наши ордера окна для полотна цены: настоящие и пробные.
 *
 * Настоящие — выборка журнала ПО РЫНКУ плюс живой стор (store/orders.marketOrders):
 * стор держит полсотни закрытых по всем рынкам, и ранние ордера окна вытеснялись
 * бы из него соседними окнами. Ключ начинается с «orders» намеренно: сигнальная
 * карточка, поставившая ордер, инвалидирует этот префикс, и выборка перечитается
 * вместе с ней.
 *
 * Пробные — из счёта окна: он и так опрашивается панелью позиции под тем же
 * ключом, и TanStack отдаёт один запрос на обоих.
 */
const HISTORY_POLL_MS = 30_000;

/** Потолок выборки на сервере (ordersPageLimit): на окно ордеров меньше. */
const HISTORY_LIMIT = 500;

export function useTimelineOrders(conditionID: string): TimelineOrders {
  // Опрос редкий: всё свежее приходит в стор по сокету, а выборка нужна только
  // за тем, что из стора уже вытеснено, — это закрытые ордера, и они не меняются.
  const history = useQuery<OrdersResponse>({
    queryKey: ["orders", "market", conditionID],
    queryFn: () => api.orders({ status: "all", market: conditionID, limit: HISTORY_LIMIT }),
    enabled: Boolean(conditionID),
    refetchInterval: HISTORY_POLL_MS,
    refetchIntervalInBackground: false,
  });
  const live = useOrders((s) => s.orders);
  const paper = useMarketPnl(conditionID).data?.paper_orders;

  return useMemo(
    () => ({
      real: marketOrders(history.data?.orders ?? [], live, conditionID),
      paper: paper ?? [],
    }),
    [history.data, live, conditionID, paper],
  );
}
